/**
 * Formats a money amount for display.
 *
 * @param amount - The amount, or null/undefined when no cost was recorded.
 * @returns A formatted string, or an em dash when there is nothing to show.
 * @remarks
 * An unrecorded cost renders as a dash rather than as zero. The API deliberately reports how many
 * items have no cost so a total is never presented as complete when it is not, and the UI must not
 * undo that by printing a confident 0.
 *
 * The currency is fixed to USD by product decision for v1 (single-currency), not as a placeholder —
 * see docs/DECISIONS.md. Real multi-currency would need a currency stored per vehicle or per user;
 * do not add that inference here without that schema.
 */
export function formatMoney(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) {
    return '—';
  }

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Describes a vehicle in one line.
 *
 * @param vehicle - The vehicle.
 * @returns The nickname when set, otherwise year, make and model.
 */
export function describeVehicle(vehicle: {
  readonly year: number;
  readonly make: string;
  readonly model: string;
  readonly nickname?: string | null;
}): string {
  const base = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

  return vehicle.nickname !== null && vehicle.nickname !== undefined && vehicle.nickname !== ''
    ? vehicle.nickname
    : base;
}
